import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { useApi, mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { fmtGregorian, fmtHijri } from "../lib/format";
import { DemoBadge, Loading, PageHero, Reveal } from "../components/Common";
import { ArticleCard } from "../components/Cards";
import Seo from "../components/Seo";
import { Unavailable } from "./NotFound";

export function News() {
  const { t, tr } = useI18n();
  const { byKind } = useSite();
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "";
  const { data, loading } = useApi(`/articles${cat ? `?category=${cat}` : ""}`);
  return (
    <div data-testid="news-page">
      <Seo title={t("news.title")} description={t("home.newsSub")} />
      <PageHero eyebrow={t("nav.news")} title={t("news.title")} sub={t("home.newsSub")} />
      <div className="container-lux py-12">
        <div className="mb-10 flex flex-wrap gap-2">
          <button type="button" data-testid="news-filter-all" onClick={() => setParams({})} className={`chip ${!cat ? "chip-on" : ""}`}>{t("news.all")}</button>
          {byKind("article_category").map((c) => (<button key={c.key} type="button" data-testid={`news-filter-${c.key}`} onClick={() => setParams({ category: c.key })} className={`chip ${cat === c.key ? "chip-on" : ""}`}>{tr(c, "label")}</button>))}
        </div>
        {loading ? <Loading /> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{data?.map((a, i) => (<Reveal key={a.id} delay={i * 0.05}><ArticleCard a={a} /></Reveal>))}</div>
        )}
      </div>
    </div>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const { t, tr, lang } = useI18n();
  const { label } = useSite();
  const { data, loading, error } = useApi(`/articles/${slug}`);
  if (loading) return <Loading />;
  if (error || !data) return <Unavailable kind="article" />;
  const a = data.article;
  const jsonLd = { "@context": "https://schema.org", "@type": "Article", headline: tr(a, "title"), image: [a.cover_image], datePublished: a.published_at, author: { "@type": "Organization", name: "Landmark Future" }, inLanguage: lang };
  return (
    <article data-testid="article-detail-page" className="pt-28">
      <Seo seo={a.seo} title={tr(a, "title")} description={tr(a, "excerpt")} image={a.cover_image} type="article" jsonLd={jsonLd} />
      <div className="container-lux max-w-4xl">
        <Link to="/news" data-testid="article-back-link" className="mb-8 inline-flex items-center gap-2 text-sm text-cta"><ArrowLeft className="rtl-flip h-4 w-4" />{t("news.back")}</Link>
        <div className="mb-5 flex flex-wrap items-center gap-3">
          {a.is_demo && <DemoBadge label={t("news.demo")} testid="article-demo-badge" />}
          <span className="eyebrow">{label("article_category", a.category)}</span>
        </div>
        <h1 data-testid="article-title" className="h-display text-4xl sm:text-5xl lg:text-6xl">{tr(a, "title")}</h1>
        <p data-testid="article-date" className="mt-5 text-sm text-fg/55">{tr(a, "author")} · <time dateTime={a.published_at}>{fmtGregorian(a.published_at, lang)}</time> · {fmtHijri(a.published_at, lang)}</p>
      </div>
      <div className="container-lux my-12 max-w-6xl"><img src={mediaUrl(a.cover_image)} alt={tr(a, "title")} className="aspect-[21/9] w-full rounded-sm object-cover" /></div>
      <div className="container-lux max-w-3xl space-y-6 pb-16">
        <p className="font-display text-2xl leading-relaxed text-fg/90">{tr(a, "excerpt")}</p>
        {a[`body_html_${lang}`] || a.body_html_en ? (
          <div data-testid="article-body-html" className="prose-lux space-y-5 text-base leading-loose text-fg/70 md:text-lg" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a[`body_html_${lang}`] || a.body_html_en) }} />
        ) : (a[`body_${lang}`]?.length ? a[`body_${lang}`] : a.body_en).map((para, i) => (<p key={i} className="text-base leading-loose text-fg/70 md:text-lg">{para}</p>))}
      </div>
      {data.related.length > 0 && (
        <section className="container-lux border-t border-fg/10 py-16">
          <h2 className="h-display mb-8 text-3xl">{t("news.related")}</h2>
          <div className="grid gap-6 md:grid-cols-3">{data.related.map((r) => (<ArticleCard key={r.id} a={r} />))}</div>
        </section>
      )}
    </article>
  );
}
